import type { ReactNode } from 'react';
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  CircleAlert,
  Minus,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { formatInActiveTimeZone } from './timezone';

export type StatusTone =
  | 'healthy'
  | 'warning'
  | 'critical'
  | 'maintenance'
  | 'stale'
  | 'offline'
  | 'informational'
  | 'neutral';

export function statusTone(value?: string | null): StatusTone {
  const normalized = String(value ?? '').toLowerCase();
  if (['healthy', 'online', 'active', 'ready', 'recovered', 'success', 'ok'].includes(normalized))
    return 'healthy';
  if (
    ['warning', 'attention_required', 'trial', 'update-available', 'degraded'].includes(normalized)
  )
    return 'warning';
  if (['critical', 'failed', 'failure', 'suspended'].includes(normalized)) return 'critical';
  if (normalized.includes('maintenance')) return 'maintenance';
  if (['stale', 'unsupported'].includes(normalized)) return 'stale';
  if (['offline', 'revoked', 'archived'].includes(normalized)) return 'offline';
  if (['observing', 'started', 'info', 'informational', 'acknowledged'].includes(normalized))
    return 'informational';
  return 'neutral';
}

export function StatusBadge({ value, label }: { value?: string | null; label?: string }) {
  const text = label ?? value ?? 'unknown';
  return (
    <span className={`status-badge tone-${statusTone(value)}`}>{text.replaceAll('_', ' ')}</span>
  );
}

export function HealthIndicator({ status, label }: { status?: string | null; label?: string }) {
  return (
    <span className={`health-indicator tone-${statusTone(status)}`}>
      <i aria-hidden="true" />
      {label ?? status ?? 'Unknown'}
    </span>
  );
}

export function PlatformBadge({ source }: { source?: string | null }) {
  const normalized = String(source ?? 'unknown').toLowerCase();
  const labels: Record<string, string> = {
    snmp: 'SNMP',
    proxmox: 'Proxmox',
    vmware: 'VMware',
    hyperv: 'Hyper-V',
    modbus: 'Modbus',
    bacnet: 'BACnet',
    'edge-http': 'HTTP',
    'edge-ping': 'Ping',
    demo: 'DEMO',
  };
  return (
    <span className={`platform-badge platform-${normalized}`}>
      {labels[normalized] ?? normalized}
    </span>
  );
}

export function TrendIndicator({
  value,
  unit = '/ day',
}: {
  value?: number | null;
  unit?: string;
}) {
  const numeric = Number(value ?? 0);
  const Icon = numeric > 0.01 ? ArrowUpRight : numeric < -0.01 ? ArrowDownRight : Minus;
  return (
    <span className={`trend ${numeric > 0.01 ? 'up' : numeric < -0.01 ? 'down' : 'flat'}`}>
      <Icon size={14} aria-hidden="true" />
      {Math.abs(numeric).toFixed(2)} {unit}
    </span>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  status,
  href,
  onNavigate,
}: {
  label: string;
  value: string | number;
  detail?: string;
  status?: string;
  href?: string;
  onNavigate?: () => void;
}) {
  return (
    <article className={`metric-card ${href ? 'metric-card-interactive' : ''}`}>
      {href && (
        <a
          className="metric-card-link"
          href={href}
          aria-label={`${label}: ${value}. ${detail ?? 'Open details'}`}
          onClick={(event) => {
            if (
              !onNavigate ||
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            )
              return;
            event.preventDefault();
            onNavigate();
          }}
        >
          <span className="sr-only">Open {label.toLowerCase()}</span>
        </a>
      )}
      <div>
        <span>{label}</span>
        {status && <i className={`metric-dot tone-${statusTone(status)}`} aria-hidden="true" />}
      </div>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </article>
  );
}

export function SectionCard({
  title,
  description,
  action,
  className = '',
  children,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`section-card ${className}`}>
      {(title || description || action) && (
        <header className="section-header">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function DataTable({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="table-scroll" role="region" aria-label={label} tabIndex={0}>
      <table className="data-table">{children}</table>
    </div>
  );
}

export function FilterBar({
  search,
  onSearch,
  placeholder = 'Search',
  children,
}: {
  search?: string;
  onSearch?: (value: string) => void;
  placeholder?: string;
  children?: ReactNode;
}) {
  return (
    <div className="filter-bar" aria-label="Table filters">
      {onSearch && (
        <label className="search-field">
          <Search size={16} aria-hidden="true" />
          <span className="sr-only">{placeholder}</span>
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={placeholder}
          />
        </label>
      )}
      {children && (
        <div className="filter-options">
          <SlidersHorizontal size={15} aria-hidden="true" />
          {children}
        </div>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Activity size={22} aria-hidden="true" />
      <strong>{title}</strong>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function LoadingState({ rows = 4 }: { rows?: number }) {
  return (
    <div className="loading-state" aria-label="Loading operational data" aria-busy="true">
      {Array.from({ length: rows }, (_, index) => (
        <span key={index} style={{ width: `${92 - (index % 3) * 11}%` }} />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-state" role="alert">
      <CircleAlert size={20} aria-hidden="true" />
      <div>
        <strong>Unable to load this view</strong>
        <p>{message}</p>
      </div>
      {onRetry && <button onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function Tabs({
  items,
  active,
  onChange,
  label,
}: {
  items: string[];
  active: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map((item) => (
        <button
          key={item}
          role="tab"
          aria-selected={active === item}
          className={active === item ? 'active' : ''}
          onClick={() => onChange(item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

export function parseApiDate(value: string) {
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
  return new Date(normalized);
}

export function relativeTime(value?: string | null) {
  if (!value) return 'Awaiting heartbeat';
  const milliseconds = Date.now() - parseApiDate(value).getTime();
  const minutes = Math.max(0, Math.floor(milliseconds / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function formatDate(value?: string | null, withTime = true) {
  if (!value) return '—';
  const date = parseApiDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatInActiveTimeZone(date, withTime);
}
